import React, { useMemo, useRef, useState } from 'react';
import api, { BASE as API_BASE } from '../../services/api';

export default function RouteCarousel({
  routes = [],
  estimates = [],
  selectedIndex = 0,
  onSelect,
  introLen,
  coreLen,
  outroLen,
  setIntroLen,
  setCoreLen,
  setOutroLen,
  onGenerateIntro,
  onStartVideo,
  onSaveRoute,
}) {
  const [preset, setPreset] = useState('custom');
  const [variation, setVariation] = useState('voice_sfx');
  const [serverPrompts, setServerPrompts] = useState(null);
  const [serverPreviewUrl, setServerPreviewUrl] = useState('');
  const containerRef = useRef(null);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  const tones = ['Bold', 'Friendly', 'Premium', 'Minimal'];

  function applyPreset(name) {
    setPreset(name);
    if (name === 'tiktok') {
      setIntroLen(11); setCoreLen(8); setOutroLen(11);
    } else if (name === 'instagram') {
      setIntroLen(5); setCoreLen(5); setOutroLen(5);
    }
    try { api.trackPageview({ path: '/onboarding/preset', referrer: document.referrer || '', anonId: undefined }); } catch {}
  }

  function haptic(ms = 50) { try { if (navigator?.vibrate) navigator.vibrate([ms]); } catch {}
  }

  function onTouchNav(dir) {
    if (typeof onSelect !== 'function') return;
    const next = Math.max(0, Math.min(routes.length - 1, selectedIndex + dir));
    if (next !== selectedIndex) onSelect(next);
  }

  function onTouchStart(e) {
    try { touchStartX.current = e.changedTouches?.[0]?.clientX || 0; } catch { touchStartX.current = 0; }
  }
  function onTouchEnd(e) {
    try { touchEndX.current = e.changedTouches?.[0]?.clientX || 0; } catch { touchEndX.current = 0; }
    const dx = touchEndX.current - touchStartX.current;
    if (Math.abs(dx) > 40) {
      onTouchNav(dx < 0 ? 1 : -1);
    }
  }

  const current = routes[selectedIndex] || null;
  const title = tones[selectedIndex] || `Route ${selectedIndex + 1}`;
  const total = (Number(introLen || 0) + Number(coreLen || 0) + Number(outroLen || 0)) | 0;

  const prompts = useMemo(() => {
    if (!current) return null;
    // Build explicit prompts structure based on route and variation
    const image = `${title}: ${current.intro.imagePrompt}`;
    const voice = `${title} VO: ${current.intro.voPrompt}`;
    const video = `${title} Video: ${current.main.videoPrompt}`;
    const sfx = (Array.isArray(current.intro.sfx) ? current.intro.sfx.map(s => s.name).join(', ') : 'whoosh');
    let director = `Intro (image + voice + SFX, ${introLen}s) → Core (video + voice + SFX, ${coreLen}s) → Outro (image + SFX, ${outroLen}s)`;
    if (variation === 'sfx_only') director = `Intro (image + SFX, ${introLen}s) → Core (video + SFX only, ${coreLen}s) → Outro (image + SFX, ${outroLen}s)`;
    if (variation === 'voice_only') director = `Intro (image + voice, ${introLen}s) → Core (video + voice, ${coreLen}s) → Outro (image + voice, ${outroLen}s)`;
    return { image, voice, video, sfx, director };
  }, [current, introLen, coreLen, outroLen, selectedIndex, variation]);

  async function handleSaveRoute() {
    try {
      if (!prompts) return;
      const res = await onSaveRoute?.({
        tone: title.toLowerCase(),
        chunks: { intro: introLen, core: coreLen, outro: outroLen },
        prompts,
        sfxLayers: (current?.intro?.sfx || []).map(s => s.name)
      });
      if (res && res.prompts) setServerPrompts(res.prompts);
      try {
        const v = res?.preview?.voiceUrl || '';
        if (v) setServerPreviewUrl(v.startsWith('/uploads/') ? `${API_BASE}${v}` : v);
      } catch {}
    } catch {}
  }

  const thumbUrl = current?.thumbnailUrl || current?.intro?.imageUrl || '';

  return (
    <div ref={containerRef} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} style={{ display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <button onClick={() => { onTouchNav(-1); }} aria-label="Prev route" style={{ background: '#111827', color: '#e5e7eb', border: '1px solid #374151', borderRadius: 8, padding: '6px 10px', cursor: 'pointer' }}>{'← Prev'}</button>
        <div style={{ color: '#e5e7eb', fontWeight: 600 }}>{title} {estimates[selectedIndex] ? `(est ~${estimates[selectedIndex].total} credits)` : ''}</div>
        <button onClick={() => { onTouchNav(1); }} aria-label="Next route" style={{ background: '#111827', color: '#e5e7eb', border: '1px solid #374151', borderRadius: 8, padding: '6px 10px', cursor: 'pointer' }}>Next →</button>
        <div style={{ color: '#9ca3af' }}>Total: {total}s</div>
      </div>

      <div style={{ display: 'grid', gap: 10, border: '1px solid #374151', borderRadius: 12, padding: 12 }}>
        {!!thumbUrl && (
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <img src={thumbUrl} loading="lazy" alt="route preview" style={{ width: 200, height: 200, objectFit: 'cover', borderRadius: 8, border: '1px solid #ff5a1f' }} />
            <div style={{ color: '#9ca3af', fontSize: 12 }}>Swipe left/right to switch routes</div>
          </div>
        )}
        <div style={{ color: '#e5e7eb' }}>Durations</div>
        <label style={{ color: '#e5e7eb' }}>Intro: {introLen}s
          <input type="range" min={5} max={15} value={introLen} onChange={(e)=>{ setIntroLen(Number(e.target.value)); haptic(25); }} style={{ width: '100%' }} />
        </label>
        <label style={{ color: '#e5e7eb' }}>Core: {coreLen}s
          <input type="range" min={5} max={15} value={coreLen} onChange={(e)=>{ setCoreLen(Number(e.target.value)); haptic(25); }} style={{ width: '100%' }} />
        </label>
        <label style={{ color: '#e5e7eb' }}>Outro: {outroLen}s
          <input type="range" min={5} max={15} value={outroLen} onChange={(e)=>{ setOutroLen(Number(e.target.value)); haptic(25); }} style={{ width: '100%' }} />
        </label>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ color: '#e5e7eb' }}>Preset
            <select value={preset} onChange={(e)=>applyPreset(e.target.value)} style={{ marginLeft: 8, padding: 6, borderRadius: 8, border: '1px solid #374151', background: '#0b0b0e', color: '#e5e7eb' }}>
              <option value="custom">Custom</option>
              <option value="tiktok">TikTok 30s (11/8/11)</option>
              <option value="instagram">Instagram 15s (5/5/5)</option>
            </select>
          </label>
          <div style={{ color: '#e5e7eb' }}>Variation</div>
          <label style={{ color: '#e5e7eb' }}><input name="var" type="radio" checked={variation==='voice_sfx'} onChange={()=>setVariation('voice_sfx')} /> Voice + SFX</label>
          <label style={{ color: '#e5e7eb' }}><input name="var" type="radio" checked={variation==='sfx_only'} onChange={()=>setVariation('sfx_only')} /> SFX Only</label>
          <label style={{ color: '#e5e7eb' }}><input name="var" type="radio" checked={variation==='voice_only'} onChange={()=>setVariation('voice_only')} /> Voice Only</label>
        </div>
      </div>

      {(serverPrompts || prompts) && (
        <div style={{ border: '1px solid #374151', borderRadius: 12, padding: 12 }}>
          <div style={{ color: '#9ca3af', marginBottom: 6 }}>Prompts</div>
          <div style={{ color: '#e5e7eb', fontSize: 12 }}>Image: {(serverPrompts || prompts).image}</div>
          <div style={{ color: '#e5e7eb', fontSize: 12 }}>Voice: {(serverPrompts || prompts).voice}</div>
          <div style={{ color: '#e5e7eb', fontSize: 12 }}>Video: {(serverPrompts || prompts).video}</div>
          <div style={{ color: '#e5e7eb', fontSize: 12 }}>SFX: {(serverPrompts || prompts).sfx}</div>
          <div style={{ color: '#e5e7eb', fontSize: 12 }}>Director: {(serverPrompts || prompts).director}</div>
          {!!serverPreviewUrl && (
            <div style={{ marginTop: 8 }}>
              <div style={{ color: '#9ca3af', marginBottom: 4 }}>Voice preview</div>
              <audio controls src={serverPreviewUrl} style={{ width: '100%' }} />
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
            <button onClick={handleSaveRoute} style={{ background: '#111827', color: '#e5e7eb', border: '1px solid #374151', borderRadius: 8, padding: '6px 10px', cursor: 'pointer' }}>Save Route</button>
            <button onClick={onGenerateIntro} style={{ background: '#00BBF9', color: '#0b0b0e', border: 0, borderRadius: 8, padding: '6px 10px', cursor: 'pointer' }}>Generate Intro</button>
            <button onClick={onStartVideo} style={{ background: '#ff5a1f', color: '#fff', border: 0, borderRadius: 8, padding: '6px 10px', cursor: 'pointer' }}>Start Video</button>
          </div>
        </div>
      )}
    </div>
  );
}
